import React, { useState, useCallback, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import TransitionFormRenderer from './TransitionFormRenderer';
import { playbookApi } from '../api/playbookApi';
import type {
  AvailableTransition,
  ExecuteTransitionResponse,
  DryRunResponse,
} from '../types/playbook.types';

interface TransitionExecutionModalProps {
  isOpen: boolean;
  instanceId: string;
  transition: AvailableTransition;
  onClose: () => void;
  onComplete: (result: ExecuteTransitionResponse) => void;
}

type ModalPhase = 'form' | 'preview' | 'executing' | 'result';

const TransitionExecutionModal: React.FC<TransitionExecutionModalProps> = ({
  isOpen,
  instanceId,
  transition,
  onClose,
  onComplete,
}) => {
  const [phase, setPhase] = useState<ModalPhase>('form');
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [dryRun, setDryRun] = useState<DryRunResponse | null>(null);
  const [result, setResult] = useState<ExecuteTransitionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const hasForms = transition.forms && transition.forms.length > 0;

  useEffect(() => {
    if (isOpen) {
      setPhase(hasForms ? 'form' : 'preview');
      setFormData({});
      setFormErrors({});
      setDryRun(null);
      setResult(null);
      setError(null);
      setLoading(false);
    }
  }, [isOpen, hasForms]);

  // Auto-load dry-run preview when entering preview phase or opening without forms
  useEffect(() => {
    if (!isOpen || phase !== 'preview') return;
    let cancelled = false;

    const loadDryRun = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await playbookApi.dryRunTransition(instanceId, transition.transitionId, { formData });
        if (!cancelled) setDryRun(res as any);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load preview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadDryRun();

    return () => { cancelled = true; };
  }, [isOpen, phase, instanceId, transition.transitionId, formData]);

  const handleFormSubmit = useCallback(() => {
    if (hasForms) {
      // Basic required field validation
      const errors: Record<string, string> = {};
      for (const form of transition.forms!) {
        const def = form.formDefinition;
        const required = Array.isArray(def.required) ? def.required as string[] : [];
        const fields = Array.isArray(def.fields) ? def.fields as Array<{ name: string }> : [];
        const requiredNames = required.length > 0 ? required : fields.filter((f: any) => f.required).map((f: any) => f.name);

        for (const name of requiredNames) {
          if (!formData[name] && formData[name] !== false && formData[name] !== 0) {
            errors[name] = 'This field is required';
          }
        }
      }
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }
      setFormErrors({});
    }
    setPhase('preview');
  }, [formData, hasForms, transition.forms]);

  const handleExecute = useCallback(async () => {
    setPhase('executing');
    setError(null);
    try {
      const res = await playbookApi.executeTransition(instanceId, transition.transitionId, {
        formData: Object.keys(formData).length > 0 ? formData : undefined,
      });
      setResult(res);
      setPhase('result');
      onComplete(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transition failed');
      setPhase('result');
    }
  }, [instanceId, transition.transitionId, formData, onComplete]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {transition.name}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
          {/* Phase: Form */}
          {phase === 'form' && hasForms && (
            <div className="space-y-4">
              {transition.forms!.map((form) => (
                <div key={form.id}>
                  {form.title && (
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{form.title}</h4>
                  )}
                  <TransitionFormRenderer
                    formDefinition={form.formDefinition}
                    value={formData}
                    onChange={setFormData}
                    errors={formErrors}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Phase: Preview (dry-run) */}
          {phase === 'preview' && (
            <div className="space-y-4">
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading preview...</span>
                </div>
              )}
              {error && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
                  {error}
                </div>
              )}
              {dryRun && !loading && (
                <>
                  <div className="rounded-md border border-lex-accent bg-lex-accent-soft px-4 py-3">
                    <p className="text-sm font-medium text-lex-accent">
                      This will move the workflow to: <strong>{dryRun.wouldTransitionTo.name}</strong>
                    </p>
                  </div>

                  {/* Actions that will execute */}
                  {dryRun.actionsSimulated.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Actions that will execute:
                      </h4>
                      <ul className="space-y-1">
                        {dryRun.actionsSimulated.map((action, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-lex-accent flex-shrink-0" />
                            <span>
                              {formatActionType(action.action_type)}
                              {action.preview && typeof action.preview === 'object' && Object.keys(action.preview).length > 0 && (
                                <span className="text-gray-400 dark:text-gray-500">
                                  {' '}&mdash; {formatPreview(action.preview)}
                                </span>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Guard results */}
                  {dryRun.guardsEvaluated.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Guard checks:</h4>
                      <ul className="space-y-1">
                        {dryRun.guardsEvaluated.map((g, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm">
                            <span className={`h-1.5 w-1.5 rounded-full ${g.passed ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                            <span className={g.passed ? 'text-gray-600 dark:text-gray-400' : 'text-rose-600 dark:text-rose-400'}>
                              {g.guard_type}: {g.reason}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Confirmation message */}
                  {transition.confirmMessage && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                      {transition.confirmMessage}
                    </div>
                  )}

                  {!dryRun.wouldSucceed && (
                    <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
                      This transition cannot proceed. Blocking issues must be resolved first.
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Phase: Executing */}
          {phase === 'executing' && (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
              <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">Executing transition...</span>
            </div>
          )}

          {/* Phase: Result */}
          {phase === 'result' && (
            <div className="space-y-4">
              {error && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
                  {error}
                </div>
              )}
              {result && result.success && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200">
                  Transition complete — now at &lsquo;{result.newStatus?.name}&rsquo;
                </div>
              )}
              {result && !result.success && result.blockedBy === 'guard' && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 dark:border-rose-800 dark:bg-rose-900/20">
                  <p className="text-sm font-medium text-rose-700 dark:text-rose-200">Blocked by guard</p>
                  <p className="text-sm text-rose-600 dark:text-rose-300 mt-1">
                    {result.guard?.errorMessage || 'A guard check prevented this transition.'}
                  </p>
                </div>
              )}
              {result && !result.success && result.blockedBy === 'validator' && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/20">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Validation errors</p>
                  <ul className="mt-1 space-y-0.5">
                    {result.validationErrors?.map((v, i) => (
                      <li key={i} className="text-sm text-amber-700 dark:text-amber-300">
                        {v.errorMessage}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result && !result.success && result.result === 'pending_approval' && (
                <div className="rounded-md border border-lex-accent bg-lex-accent-soft px-4 py-3">
                  <p className="text-sm font-medium text-lex-accent">
                    Approval requested — awaiting step {(result.pendingApproval?.currentStep ?? 0) + 1} of {result.pendingApproval?.totalSteps}
                  </p>
                </div>
              )}
              {result && !result.success && result.result === 'rejected' && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 dark:border-rose-800 dark:bg-rose-900/20">
                  <p className="text-sm font-medium text-rose-700 dark:text-rose-200">Transition rejected</p>
                  {result.comment && (
                    <p className="text-sm text-rose-600 dark:text-rose-300 mt-1">{result.comment}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          {phase === 'form' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleFormSubmit}
                className="rounded-md bg-lex-accent px-3 py-1.5 text-sm font-medium text-lex-accent-contrast hover:bg-lex-accent-hover"
              >
                Continue
              </button>
            </>
          )}
          {phase === 'preview' && (
            <>
              <button
                type="button"
                onClick={hasForms ? () => setPhase('form') : onClose}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {hasForms ? 'Back' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleExecute}
                disabled={loading || (dryRun ? !dryRun.wouldSucceed : false)}
                className="rounded-md bg-lex-accent px-3 py-1.5 text-sm font-medium text-lex-accent-contrast hover:bg-lex-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                Execute Transition
              </button>
            </>
          )}
          {phase === 'result' && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-lex-accent px-3 py-1.5 text-sm font-medium text-lex-accent-contrast hover:bg-lex-accent-hover"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

function formatActionType(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatPreview(preview: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, val] of Object.entries(preview)) {
    if (typeof val === 'string' || typeof val === 'number') {
      parts.push(`${key}: ${val}`);
    }
  }
  return parts.join(', ');
}

export default TransitionExecutionModal;
